import { useCallback, useEffect, useState, type ChangeEvent } from 'react'
import Box from '@mui/material/Box'
import Paper from '@mui/material/Paper'
import Typography from '@mui/material/Typography'
import Button from '@mui/material/Button'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import TablePagination from '@mui/material/TablePagination'
import CircularProgress from '@mui/material/CircularProgress'
import Alert from '@mui/material/Alert'
import Chip from '@mui/material/Chip'
import IconButton from '@mui/material/IconButton'
import Tooltip from '@mui/material/Tooltip'
import ToggleButton from '@mui/material/ToggleButton'
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup'
import AddIcon from '@mui/icons-material/Add'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined'
import { api } from '../../../shared/services/API'
import { useNotification } from '../../../shared/contexts/NotificationContext'
import { getBackendError } from '../../../shared/utils/formErrors'
import type { Page } from '../../../shared/utils/types'
import { useTablePagination } from '../../../shared/hooks/useTablePagination'
import {
    CICLO_LABEL,
    ESTADO_SUBSCRICAO_LABEL,
    ESTADO_SUBSCRICAO_TONE,
    formatDate,
    formatMoney,
    type EstadoSubscricao,
    type Subscricao,
} from '../subscricoesModel'
import SubscricaoFormDialog from '../components/SubscricaoFormDialog'

const FILTROS: (EstadoSubscricao | 'TODAS')[] = ['TODAS', 'ACTIVA', 'SUSPENSA', 'CANCELADA']

export default function SubscricoesList() {
    const { notify } = useNotification()

    const [rows, setRows] = useState<Subscricao[]>([])
    const [total, setTotal] = useState(0)
    const { page, setPage, rowsPerPage, setRowsPerPage } = useTablePagination('platform-subscricoes:table-page', 20)
    const [estado, setEstado] = useState<EstadoSubscricao | 'TODAS'>('TODAS')
    const [isLoading, setIsLoading] = useState(true)
    const [error, setError] = useState('')

    const [formOpen, setFormOpen] = useState(false)
    const [editing, setEditing] = useState<Subscricao | null>(null)

    const fetch = useCallback(() => {
        setIsLoading(true)
        setError('')
        api.get<Page<Subscricao>>('/api/admin/subscricoes', {
            params: {
                page,
                size: rowsPerPage,
                sort: 'createdAt,desc',
                ...(estado !== 'TODAS' ? { estado } : {}),
            },
        })
            .then((r) => {
                setRows(r.data.content ?? [])
                setTotal(r.data.totalElements ?? 0)
            })
            .catch(() => setError('Não foi possível carregar as subscrições.'))
            .finally(() => setIsLoading(false))
    }, [page, rowsPerPage, estado])

    useEffect(() => {
        // eslint-disable-next-line react-hooks/set-state-in-effect -- fetch alterna a sua própria flag de loading
        fetch()
    }, [fetch])

    const handleChangePage = (_: unknown, next: number) => setPage(next)
    const handleChangeRowsPerPage = (e: ChangeEvent<HTMLInputElement>) => {
        setRowsPerPage(parseInt(e.target.value, 10))
        setPage(0)
    }

    const cancelar = async (s: Subscricao) => {
        if (!window.confirm(`Cancelar a subscrição de "${s.tenantNome ?? s.tenantId}"?`)) return
        try {
            await api.post(`/api/admin/subscricoes/${s.id}/cancelar`)
            notify('Subscrição cancelada.')
            fetch()
        } catch (err) {
            notify(getBackendError(err)?.message ?? 'Não foi possível cancelar.', 'error')
        }
    }

    return (
        <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2, mb: 3 }}>
                <Box>
                    <Typography variant="h5" sx={{ fontWeight: 700 }}>Subscrições</Typography>
                    <Typography variant="body2" color="text.secondary">{total} {total === 1 ? 'subscrição' : 'subscrições'}</Typography>
                </Box>
                <Button variant="contained" startIcon={<AddIcon />} onClick={() => { setEditing(null); setFormOpen(true) }}>
                    Nova subscrição
                </Button>
            </Box>

            <ToggleButtonGroup
                size="small"
                exclusive
                value={estado}
                onChange={(_, v) => { if (v) { setEstado(v); setPage(0) } }}
                sx={{ mb: 2, flexWrap: 'wrap' }}
            >
                {FILTROS.map((f) => (
                    <ToggleButton key={f} value={f} sx={{ textTransform: 'none' }}>
                        {f === 'TODAS' ? 'Todas' : ESTADO_SUBSCRICAO_LABEL[f]}
                    </ToggleButton>
                ))}
            </ToggleButtonGroup>

            {error && <Alert severity="error" sx={{ borderRadius: 2, mb: 2 }}>{error}</Alert>}

            <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'divider' }}>
                <TableContainer>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Empresa</TableCell>
                                <TableCell>Plano</TableCell>
                                <TableCell>Ciclo</TableCell>
                                <TableCell align="right">Preço</TableCell>
                                <TableCell>Próxima faturação</TableCell>
                                <TableCell>Estado</TableCell>
                                <TableCell align="right">Acções</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {isLoading ? (
                                <TableRow><TableCell colSpan={7} align="center" sx={{ py: 6 }}><CircularProgress size={24} /></TableCell></TableRow>
                            ) : rows.length === 0 ? (
                                <TableRow><TableCell colSpan={7} align="center" sx={{ py: 6, color: 'text.secondary' }}>Nenhuma subscrição.</TableCell></TableRow>
                            ) : (
                                rows.map((s) => (
                                    <TableRow key={s.id} hover>
                                        <TableCell sx={{ fontWeight: 600 }}>{s.tenantNome ?? s.tenantId}</TableCell>
                                        <TableCell>{s.planoNome ?? '—'}</TableCell>
                                        <TableCell>{CICLO_LABEL[s.cicloFaturacao]}</TableCell>
                                        <TableCell align="right">{formatMoney(s.preco)}</TableCell>
                                        <TableCell sx={{ color: 'text.secondary' }}>{formatDate(s.proximaFaturacaoEm)}</TableCell>
                                        <TableCell>
                                            <Chip
                                                size="small"
                                                label={ESTADO_SUBSCRICAO_LABEL[s.estado]}
                                                sx={{ fontWeight: 600, ...ESTADO_SUBSCRICAO_TONE[s.estado] }}
                                            />
                                        </TableCell>
                                        <TableCell align="right">
                                            <Tooltip title="Editar">
                                                <IconButton size="small" onClick={() => { setEditing(s); setFormOpen(true) }} aria-label="Editar">
                                                    <EditOutlinedIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title={s.estado === 'CANCELADA' ? 'Já cancelada' : 'Cancelar'}>
                                                <span>
                                                    <IconButton
                                                        size="small"
                                                        sx={{ color: 'error.main' }}
                                                        disabled={s.estado === 'CANCELADA'}
                                                        onClick={() => cancelar(s)}
                                                        aria-label="Cancelar"
                                                    >
                                                        <CancelOutlinedIcon fontSize="small" />
                                                    </IconButton>
                                                </span>
                                            </Tooltip>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
                <TablePagination
                    component="div"
                    count={total}
                    page={page}
                    rowsPerPage={rowsPerPage}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    rowsPerPageOptions={[10, 20, 50]}
                    labelRowsPerPage="Linhas por página"
                />
            </Paper>

            <SubscricaoFormDialog
                open={formOpen}
                initial={editing}
                onClose={() => setFormOpen(false)}
                onSaved={() => { setFormOpen(false); notify(editing ? 'Subscrição actualizada.' : 'Subscrição criada.'); fetch() }}
            />
        </Box>
    )
}
